// Sector defaults (docs/specs/NAVIGATION_2026_10.md "Sector defaults"; migration 20270227120000). The owner says what
// kind of business it is and Ogma's settings start from that sector's values. The database owns the table
// (crm__sector_defaults); SECTOR_DEFAULTS is the app's copy, checked against the migration by sectors.test.ts, so the
// app can say which settings are still the defaults and the demo can apply them.
import type { Json, JsonObject } from "./types";
import { FIRST_ASK_LABELS } from "./settingsForms";
import type { BlockKey, PathSteps, TemplateKey } from "./paths";

export const SECTOR_KEYS = ["it_msp", "ticc", "weddings", "b2b_services", "b2b_startup", "b2c_home_improvement", "other"] as const;
export type SectorKey = (typeof SECTOR_KEYS)[number];

export const SECTOR_LABELS: Record<SectorKey, string> = {
  it_msp: "IT managed services",
  ticc: "Testing, inspection and certification",
  weddings: "Weddings and events",
  b2b_services: "Other services to businesses",
  b2b_startup: "A start-up selling to businesses",
  b2c_home_improvement: "Home improvement for households",
  other: "Something else",
};

export interface SectorSetting {
  path: string[];
  /** Values the setting held before sectors (the engine's old defaults): still a default. */
  known: Json[];
  base: Json;
  sectors: Partial<Record<SectorKey, Json>>;
}

export const SECTOR_DEFAULTS: { sectors: SectorKey[]; quotes_on: SectorKey[]; settings: SectorSetting[] } = {
  sectors: [...SECTOR_KEYS],
  quotes_on: ["b2c_home_improvement"],
  settings: [
    {
      path: ["channels", "email", "send_window"],
      known: [{ windows: [{ days: "weekdays", start: "08:00", end: "17:00" }], timezone: "recipient" }],
      base: { windows: [{ days: ["Tue", "Wed", "Thu"], start: "08:00", end: "10:00" }], timezone: "recipient" },
      sectors: { weddings: { windows: [{ days: "weekdays", start: "10:00", end: "14:00" }], timezone: "recipient" } },
    },
    {
      path: ["voice", "tone"],
      known: [""],
      base: "Plain and direct, under 90 words",
      sectors: { ticc: "Formal and precise, under 90 words", weddings: "Warm and friendly, under 90 words" },
    },
    {
      path: ["connect", "flavours", "find", "first_ask"],
      known: ["call"],
      base: "call",
      sectors: { weddings: "coffee", b2c_home_improvement: "quote" },
    },
    {
      path: ["find", "follow_ups"],
      known: [],
      base: 2,
      sectors: { it_msp: 3, ticc: 3, b2c_home_improvement: 1 },
    },
  ],
};

/** What each setting is called, where the owner changes it, and its value in words. */
export const SETTING_WORDS: Record<string, { label: string; page: string; say: (v: Json | undefined) => string }> = {
  "channels.email.send_window": { label: "When emails go out", page: "/settings/sending/times", say: sayWindow },
  "voice.tone": { label: "Tone", page: "/campaigns/defaults", say: (v) => (typeof v === "string" && v ? v : "Not set") },
  "connect.flavours.find.first_ask": {
    label: "What you ask for first",
    page: "/settings/business/paths",
    say: (v) => (typeof v === "string" && v in FIRST_ASK_LABELS ? FIRST_ASK_LABELS[v as keyof typeof FIRST_ASK_LABELS] : "Not set"),
  },
  "find.follow_ups": { label: "Follow-ups in a new campaign", page: "/campaigns/list", say: (v) => (typeof v === "number" ? `${v} after the first email` : "Not set") },
};

const same = (a: Json | undefined, b: Json | undefined) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function at(config: JsonObject | null | undefined, path: string[]): Json | undefined {
  let node: Json | undefined = config ?? undefined;
  for (const p of path) {
    if (!node || typeof node !== "object" || Array.isArray(node)) return undefined;
    node = (node as JsonObject)[p];
  }
  return node;
}

export function isSectorKey(v: unknown): v is SectorKey {
  return typeof v === "string" && (SECTOR_KEYS as readonly string[]).includes(v);
}

/** The business's sector, kept with its core. */
export function sectorOf(config: JsonObject | null | undefined): SectorKey | null {
  const v = at(config, ["core", "sector"]);
  return isSectorKey(v) ? v : null;
}

/** The sector's value for a setting. */
export const sectorValue = (s: SectorSetting, sector: SectorKey): Json => s.sectors[sector] ?? s.base;

/** Whether a setting still holds a default (as crm__sector_is_default): empty, an old default, the base or a sector's. */
export function isDefaultValue(s: SectorSetting, v: Json | undefined): boolean {
  return v === undefined || v === null || s.known.some((k) => same(k, v)) || same(s.base, v) || Object.values(s.sectors).some((x) => same(x, v));
}

export interface SettingRow {
  key: string;
  label: string;
  page: string;
  now: string;
  sector: string;
  /** "default": what the sector sets; "changed": the owner's own; "pending": a default the sector will move. */
  state: "default" | "changed" | "pending";
}

/** Each sector setting against the business's settings, for Settings › Your business. */
export function settingRows(config: JsonObject | null | undefined, sector: SectorKey): SettingRow[] {
  return SECTOR_DEFAULTS.settings.map((s) => {
    const key = s.path.join(".");
    const words = SETTING_WORDS[key]!;
    const v = at(config, s.path);
    const want = sectorValue(s, sector);
    const state = same(v, want) ? "default" : isDefaultValue(s, v) ? "pending" : "changed";
    return { key, label: words.label, page: words.page, now: words.say(v), sector: words.say(want), state };
  });
}

/** The settings with the sector applied, as crm_apply_sector_defaults makes them (the demo uses this). */
export function applySector(config: JsonObject, sector: SectorKey, force = false): { config: JsonObject; fields: string[] } {
  const next = structuredClone(config) as JsonObject;
  const fields: string[] = [];
  for (const s of SECTOR_DEFAULTS.settings) {
    const v = at(next, s.path);
    const want = sectorValue(s, sector);
    if ((force || isDefaultValue(s, v)) && !same(v, want)) {
      let node = next;
      s.path.forEach((p, i) => {
        if (i === s.path.length - 1) node[p] = want;
        else {
          if (!node[p] || typeof node[p] !== "object" || Array.isArray(node[p])) node[p] = {};
          node = node[p] as JsonObject;
        }
      });
      fields.push(s.path.join("."));
    }
  }
  const core = (next.core && typeof next.core === "object" && !Array.isArray(next.core) ? next.core : {}) as JsonObject;
  next.core = { ...core, sector };
  return { config: next, fields };
}

/** A new campaign's follow-ups: the sector's, or none set. */
export function followUpsFrom(config: JsonObject | null | undefined): number | null {
  const v = at(config, ["find", "follow_ups"]);
  return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 6 ? v : null;
}

function sayWindow(v: Json | undefined): string {
  const w = v && typeof v === "object" && !Array.isArray(v) ? (v as JsonObject).windows : undefined;
  if (!Array.isArray(w) || !w.length) return "Not set";
  return w
    .map((x) => {
      const o = (x ?? {}) as JsonObject;
      const days = Array.isArray(o.days) ? (o.days as string[]).join(", ") : o.days === "weekdays" ? "Weekdays" : o.days === "weekends" ? "Weekends" : "Every day";
      return `${days}, ${String(o.start ?? "")} to ${String(o.end ?? "")}`;
    })
    .join("; ");
}

// ------------------------------------------------------------------ recommended steps after a reply (31 Oct 2026)
/** Ogma's recommended steps after a reply for one sector (docs/specs/CAMPAIGN_STEPS_2026_11.md A3): the first reply and
 * how fast it goes, then the steps in order. A campaign copies them into its own path, or starts from them. */
export interface RecommendedSteps {
  template: TemplateKey;
  blocks: BlockKey[];
  steps: PathSteps;
}

const offerTimes = (hours: number): PathSteps => ({ reply: { mode: "offer_times", channels: ["email"], due_hours: hours } });

export const SECTOR_REPLY_STEPS: Record<SectorKey, RecommendedSteps> = {
  it_msp: { template: "intro_call", blocks: ["book", "meet", "recap", "proposal", "accept", "follow_up"], steps: { ...offerTimes(1), recap: { mode: "actions" } } },
  ticc: { template: "intro_call", blocks: ["book", "meet", "recap", "quote", "accept", "follow_up"], steps: { ...offerTimes(1), recap: { mode: "actions" } } },
  // A brochure needs the business's own guide, so the first reply acknowledges until one is chosen.
  weddings: { template: "intro_call", blocks: ["qualify", "book", "meet", "quote", "accept", "follow_up"], steps: { reply: { mode: "acknowledge", channels: ["email"], due_hours: 1 } } },
  b2b_services: { template: "intro_call", blocks: ["book", "meet", "recap", "proposal", "accept", "follow_up"], steps: { ...offerTimes(4), recap: { mode: "highlight" } } },
  b2b_startup: { template: "intro_call", blocks: ["book", "meet", "recap", "follow_up"], steps: { reply: { mode: "booking_link", channels: ["email"], due_hours: 1 } } },
  // An offer once a household has gone quiet for two weeks after its quote (9.31, within the save offer).
  b2c_home_improvement: { template: "quote_from_known", blocks: ["gather", "quote", "accept", "make_offer", "follow_up"], steps: { reply: { mode: "acknowledge", channels: ["email"], due_hours: 0.25 }, make_offer: { wait_days: 14 } } },
  other: { template: "intro_call", blocks: ["book", "meet", "recap", "follow_up"], steps: offerTimes(4) },
};

/** The recommended steps for a business: its sector's, else "Something else". */
export const recommendedSteps = (config: JsonObject | null | undefined): RecommendedSteps => SECTOR_REPLY_STEPS[sectorOf(config) ?? "other"];
